import { SortingState } from "@tanstack/react-table"

import { ScheduleHorizon } from "@constants/scheduleHorizon"

export type CardsViewConfig = {
    sorting: SortingState
    attributeVisibility: Record<string, boolean>
    scheduleHorizon?: ScheduleHorizon
}

export type CardsViewConfigType = {
    version?: string
    cards: {
        [key: string]: CardsViewConfig | undefined
    }
}

const defaultCardsViewConfig: CardsViewConfigType = {
    /*
        Versioning Scheme:
        - Major: Breaking changes.
        - Minor: Attribute additions/removals.
        - Patch: Updates to the default config.
        Note: Patch updates are only applied to users without modified view configs, serving as a new default without data migration.
    */
    version: "1.2.0",
    cards: {
        // Jobs
        jobs_cards__open_all_open: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_pending: {
            sorting: [{ id: "id", desc: true }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_scheduled: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_in_progress: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_on_hold: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_completed: {
            sorting: [{ id: "last_completed_at", desc: true }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__closed_all_closed: {
            sorting: [{ id: "id", desc: true }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
        },
        jobs_cards__closed_finalized: {
            sorting: [{ id: "last_finalized_at", desc: true }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
        },
        jobs_cards__closed_cancelled: {
            sorting: [{ id: "start_time", desc: true }],
            attributeVisibility: {
                assigned_technicians: true,
                start_time: true,
                duration: true,
                priority: true,
                recall_to: true,
                series: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Estimates
        estimates_cards__open_all_open: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__open_pending: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__open_sent: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__open_approved: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__closed_all_closed: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__closed_won: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__closed_lost: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        estimates_cards__closed_cancelled: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                down_payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Invoices
        invoices_cards__open_all_open: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__open_pending: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__open_outstanding: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__open_overdue: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__closed_all_closed: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__closed_paid: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__closed_voided: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        invoices_cards__closed_forgiven: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                total: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Purchase Orders
        purchase_orders_cards__open_all_open: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__open_pending: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__open_open: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__open_in_transit: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__open_delivered: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__open_stocked: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__closed_all_closed: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__closed_paid: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },
        purchase_orders_cards__closed_cancelled: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                item_count: true,
                associated_estimates: true,
                associated_jobs: true,
                associated_invoices: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Pricebook Items - Services
        pricebook_items_services_cards__active_all_active: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                default_job_duration: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_services_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                default_job_duration: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_services_cards__active_active: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                default_job_duration: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_services_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                default_job_duration: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Pricebook Items - Parts & Materials
        "pricebook_items_parts_&_materials_cards__active_all_active": {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                track_inventory: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
                lowest_inventory_status: true,
                inventory_value: true,
                total_quantity_on_order: true,
            },
        },
        "pricebook_items_parts_&_materials_cards__active_review": {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                track_inventory: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
                lowest_inventory_status: true,
                inventory_value: true,
                total_quantity_on_order: true,
            },
        },
        "pricebook_items_parts_&_materials_cards__active_active": {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                track_inventory: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
                lowest_inventory_status: true,
                inventory_value: true,
                total_quantity_on_order: true,
            },
        },
        "pricebook_items_parts_&_materials_cards__inactive_all_inactive": {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                track_inventory: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
                lowest_inventory_status: true,
                inventory_value: true,
                total_quantity_on_order: true,
            },
        },

        // Pricebook Items - Miscellaneous
        pricebook_items_miscellaneous_cards__active_all_active: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_miscellaneous_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_miscellaneous_cards__active_active: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_miscellaneous_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                default_is_taxable: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Pricebook Items - Discounts
        pricebook_items_discounts_cards__active_all_active: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_discounts_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_discounts_cards__active_active: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_discounts_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },

        // Pricebook Items - Tax Rates
        pricebook_items_tax_rates_cards__active_all_active: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_tax_rates_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_tax_rates_cards__active_active: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        pricebook_items_tax_rates_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                default_price: true,
                last_modified_at: true,
                labels: true,
            },
        },
        // Vendors
        vendors_cards__active_all_active: {
            sorting: [{ id: "name", desc: false }],
            attributeVisibility: {
                outstanding_commitments: true,
                current_purchase_order_count: true,
                amount_owed: true,
                payment_terms: true,
                last_modified_at: true,
                labels: true,
            },
        },
        vendors_cards__inactive_all_inactive: {
            sorting: [{ id: "name", desc: false }],
            attributeVisibility: {
                outstanding_commitments: true,
                current_purchase_order_count: true,
                amount_owed: true,
                payment_terms: true,
                last_modified_at: true,
                labels: true,
            },
        },
        // Clients
        clients_cards__active_all_active: {
            sorting: [{ id: "name", desc: false }],
            attributeVisibility: {
                credit_limit: true,
                service_locations_count: true,
                default_invoice_net: true,
                industry_type_label: true,
                last_modified_at: true,
                labels: true,
            },
        },
        clients_cards__inactive_all_inactive: {
            sorting: [{ id: "name", desc: false }],
            attributeVisibility: {
                credit_limit: true,
                service_locations_count: true,
                default_invoice_net: true,
                industry_type_label: true,
                last_modified_at: true,
                labels: true,
            },
        },
        // Job Series
        job_series_cards__open_all_open: {
            sorting: [
                { id: "recurrence_start", desc: false },
                { id: "created_at", desc: false },
            ],
            attributeVisibility: {
                type_label: true,
                recurrence_interval: true,
                recurrence_start: true,
                recurrence_end: true,
                completion_progress: true,
                open_jobs_count: true,
            },
        },
        job_series_cards__closed_all_closed: {
            sorting: [
                { id: "recurrence_start", desc: true },
                { id: "created_at", desc: true },
            ],
            attributeVisibility: {
                type_label: true,
                recurrence_interval: true,
                recurrence_start: true,
                recurrence_end: true,
                completion_progress: true,
                open_jobs_count: true,
            },
        },
        // Bills
        bills_cards__open_all_open: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        bills_cards__open_pending: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        bills_cards__open_outstanding: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        bills_cards__open_overdue: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        bills_cards__closed_all_closed: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        bills_cards__closed_paid: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        bills_cards__closed_voided: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                terms: true,
                payment_status: true,
                last_modified_at: true,
                labels: true,
            },
        },
        // Credit Notes
        credit_notes_cards__open_all_open: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                credit_note_type_label: true,
                total: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        credit_notes_cards__open_pending: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                credit_note_type_label: true,
                total: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        credit_notes_cards__closed_all_closed: {
            sorting: [{ id: "date_issued", desc: true }],
            attributeVisibility: {
                credit_note_type_label: true,
                total: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        credit_notes_cards__closed_issued: {
            sorting: [{ id: "date_issued", desc: true }],
            attributeVisibility: {
                credit_note_type_label: true,
                total: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        credit_notes_cards__closed_voided: {
            sorting: [{ id: "date_issued", desc: true }],
            attributeVisibility: {
                credit_note_type_label: true,
                total: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        // Vendor Credits
        vendor_credits_cards__open_all_open: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        vendor_credits_cards__open_pending: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        vendor_credits_cards__open_recorded: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        vendor_credits_cards__closed_all_closed: {
            sorting: [{ id: "date_issued", desc: true }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        vendor_credits_cards__closed_consumed: {
            sorting: [{ id: "date_issued", desc: true }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
        vendor_credits_cards__closed_voided: {
            sorting: [{ id: "date_issued", desc: true }],
            attributeVisibility: {
                subtotal: true,
                date_issued: true,
                last_modified_at: true,
                labels: true,
            },
        },
    },
}

export default defaultCardsViewConfig
